import { SiteNav, SiteFooter } from '@/components/ui/SiteNav'
import { AlertCircle } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/Button'

const TOOLS = [
  {
    id: 'surveymonkey',
    label: 'SurveyMonkey',
    guide: [
      'Go to Analyze Results → Export All → CSV',
      'Choose "All response data" (not summary data)',
      'Download the zip and use the CSV inside',
    ],
    gotcha: 'SurveyMonkey exports two header rows — Echosome merges them automatically.',
  },
  {
    id: 'qualtrics',
    label: 'Qualtrics',
    guide: [
      'Go to Data & Analysis → Export & Import → Export Data',
      'Choose CSV or Excel, and check "Use choice text"',
      'Download the file and upload it directly',
    ],
    gotcha: 'Qualtrics adds 3 metadata rows at the top — automatically detected and skipped.',
  },
  {
    id: 'typeform',
    label: 'Typeform',
    guide: [
      'Open your form, go to Results → Responses',
      'Click Download → CSV',
      'All responses export in a single file',
    ],
    gotcha: null,
  },
  {
    id: 'googleforms',
    label: 'Google Forms',
    guide: [
      'Open the linked Google Sheet for your form',
      'File → Download → Comma-separated values (.csv)',
    ],
    gotcha: null,
  },
  {
    id: 'excel',
    label: 'Excel / CSV',
    guide: [
      'Save your data as .xlsx or .csv',
      'First row must be column headers',
      'UTF-8 encoding recommended for special characters',
    ],
    gotcha: null,
  },
]

export function IntegrationsPublicPage() {
  return (
    <div className="min-h-screen bg-paper">
      <SiteNav />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-16 space-y-16">

        {/* Header */}
        <div className="space-y-4">
          <p className="text-sm font-medium text-echo-indigo uppercase tracking-wide">Import guides</p>
          <h1 className="text-4xl font-medium text-deep-ink leading-tight" style={{ letterSpacing: '-0.02em' }}>
            Export your survey data — then upload it in seconds
          </h1>
          <p className="text-lg text-slate-600 leading-relaxed">
            Echosome accepts exports from every major survey platform. Select your tool below for exact export
            steps and known format quirks our parser handles automatically.
          </p>
        </div>

        {/* Per-tool sections */}
        {TOOLS.map((tool) => (
          <section key={tool.id} className="space-y-4">
            <h2 className="text-xl font-medium text-deep-ink">{tool.label}</h2>
            <ol className="space-y-2 pl-5 list-decimal">
              {tool.guide.map((step, i) => (
                <li key={i} className="text-slate-700 text-sm leading-relaxed">{step}</li>
              ))}
            </ol>
            {tool.gotcha !== null && (
              <div className="flex items-start gap-2 mt-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <AlertCircle size={16} className="text-amber-600 mt-0.5 shrink-0" />
                <p className="text-sm text-amber-800">{tool.gotcha}</p>
              </div>
            )}
          </section>
        ))}

        {/* Bottom CTA */}
        <div className="border border-border-gray rounded-xl p-6 space-y-3">
          <p className="text-sm font-medium text-echo-indigo uppercase tracking-wide">Ready to upload?</p>
          <h2 className="text-2xl font-medium text-deep-ink">Bring your data — we handle the format</h2>
          <p className="text-slate-600 text-sm leading-relaxed">
            Upload your export and Echosome builds a queryable twin panel from your respondents automatically.
          </p>
          <div className="pt-1">
            <Link to="/signup">
              <Button variant="primary" size="sm">Start free</Button>
            </Link>
          </div>
        </div>

      </main>
      <SiteFooter />
    </div>
  )
}
